import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import { getFromLocalStorage } from '../utils/localStorage';
import AdminTopNavbar from './components/AdminTopNavbar';
import {
  getAllUsers,
  updateUser,
  deleteUser,
  addUser
} from '../api/adminApi';
import {
  Menu, X, User, LogOut, Home, Grid3X3, Users, Phone, Search, Filter,
  Edit, Trash2, UserCheck, UserX, Shield, Eye, EyeOff, ChevronLeft, ChevronRight
} from 'lucide-react';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    isActive: true,
    role: 'user',
    isVerified: false
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [filtersState, setFiltersState] = useState({
    search: '',
    status: 'all',
    role: 'all',
    verification: 'all'
  });
  const [totalUsers, setTotalUsers] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const navigate = useNavigate();

  // Authentication state management
  // Admin components only check admin authentication
  const adminToken = getFromLocalStorage("adminToken");
  const isLoggedIn = (adminToken && adminToken.trim() !== "");

  const handleLogout = () => {
    // Only clear admin tokens from admin components
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminRole');
    toast.success("Admin logged out successfully!");
    window.location.href = '/admin/login';
  };

  // Fetch users with error handling and loading states
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersResponse = await getAllUsers();

      const usersWithDefaults = usersResponse.map(user => {
        let role = 'user';
        if (user.user_type === 'creator') {
          role = user.membershipPlan === 'pro' ? 'creator_pro' : 'creator';
        }

        return {
          ...user,
          _id: user._id,
          name: user.name || user.username || user.emailId?.split('@')[0] || 'User',
          email: user.email || user.emailId || '',
          role: role,
          isActive: user.isActive !== undefined ? user.isActive : true,
          isVerified: user.isVerified !== undefined ? user.isVerified : false,
          createdAt: user.createdAt || new Date().toISOString(),
          user_type: user.user_type || 'viewer',
          membershipPlan: user.membershipPlan || 'free'
        };
      });

      if (Array.isArray(usersResponse)) {
        setUsers(usersWithDefaults);
        setFilteredUsers(usersWithDefaults);
        setTotalUsers(usersWithDefaults.length);
      } else {
        console.error('Invalid users data format:', usersResponse);
        setError('Invalid data format received from server');
        setUsers([]);
        setFilteredUsers([]);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
      toast.error('Failed to load users');
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search and filter criteria
  const applyFilters = () => {
    let result = [...users];

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(user =>
        user.name?.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      result = result.filter(user => user.isActive === isActive);
    }

    // Apply role filter
    if (roleFilter !== 'all') {
      result = result.filter(user => user.role === roleFilter);
    }

    // Apply verification filter
    if (verificationFilter !== 'all') {
      const isVerified = verificationFilter === 'verified';
      result = result.filter(user => user.isVerified === isVerified);
    }

    setFilteredUsers(result);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Call applyFilters whenever filters or search term changes
  useEffect(() => {
    if (users.length > 0) {
      applyFilters();
    }
  }, [searchTerm, statusFilter, roleFilter, verificationFilter, users]);

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const handleToggleActive = async (user) => {
    try {
      const updatedData = { isActive: !user.isActive };
      await updateUser(user.email, updatedData);
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u._id === user._id ? { ...u, isActive: !user.isActive } : u
        )
      );
      toast.success(`User ${!user.isActive ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      console.error('Toggle status error:', err);
      toast.error(err.message || 'Failed to update user status');
    }
  };

  const handleToggleVerification = async (user) => {
    try {
      const updatedData = { isVerified: !user.isVerified };
      await updateUser(user.email, updatedData);
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u._id === user._id ? { ...u, isVerified: !user.isVerified } : u
        )
      );
      toast.success(`User ${!user.isVerified ? 'verified' : 'unverified'} successfully`);
    } catch (err) {
      console.error('Toggle verification error:', err);
      toast.error(err.message || 'Failed to update user verification');
    }
  };

  const handleEditUser = (user) => {
    setEditFormData({
      name: user.name || '',
      email: user.email || '',
      isActive: user.isActive,
      role: user.role,
      isVerified: user.isVerified || false
    });
    setUserToEdit(user);
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!userToEdit) return;

    try {
      setActionLoading(true);
      const updateData = {
        name: editFormData.name,
        isActive: editFormData.isActive,
        role: editFormData.role,
        isVerified: editFormData.isVerified,
        email: editFormData.email,
        // Map creator_pro to backend fields
        ...(editFormData.role === 'creator_pro' ? {
          user_type: 'creator',
          membershipPlan: 'pro'
        } : editFormData.role === 'creator' ? {
          user_type: 'creator',
          membershipPlan: 'free'
        } : {
          user_type: 'viewer',
          membershipPlan: 'free'
        })
      };

      // Use the ORIGINAL user email to find the user in the database
      // (not the new email from the form which might have been changed)
      const originalEmail = userToEdit.email || userToEdit.emailId;
      if (!originalEmail) {
        throw new Error('Original user email not found');
      }

      console.log('Updating user:', { originalEmail, updateData });

      // Update the user using their ORIGINAL email
      const updatedUser = await updateUser(originalEmail, updateData);

      // Refresh the entire user list to get the latest data from backend
      await fetchUsers();

      toast.success('User updated successfully');
      setShowEditModal(false);
      setUserToEdit(null);

    } catch (err) {
      console.error('Update user error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update user';
      toast.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setUserToEdit(null);
    setEditFormData({ name: '', email: '', isActive: true, role: 'user', isVerified: false });
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setActionLoading(true);
      await deleteUser(userToDelete._id);
      setUsers(prevUsers => prevUsers.filter(user => user._id !== userToDelete._id));
      toast.success('User deleted successfully');
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (err) {
      toast.error(err.message || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  // Handle user registration via public signup so user can login immediately
  const handleRegister = async (userData) => {
    try {
      setActionLoading(true);
      const username = userData.username || (userData.name?.trim().toLowerCase().replace(/\s+/g, '') || (userData.emailId || '').split('@')[0]);

      // Step 1: create user with public signup so hashing/credential flow matches login API
      const signupPayload = { name: userData.name, emailId: userData.emailId, password: userData.password, username };
      const signupResp = await import('../api/apis').then(m => m.signup(signupPayload));
      const created = signupResp?.data;

      // Step 2: immediately verify + set role via admin endpoint (so they can login right away)
      await updateUser(userData.emailId, { isVerified: true, isActive: true, role: userData.role, name: userData.name, email: userData.emailId });

      const newUser = created || { email: userData.emailId, name: userData.name, isActive: true, isVerified: true, user_type: (userData.role === 'creator' ? 'creator' : 'viewer') };

      // Ensure we have all required fields with proper fallbacks
      const userWithDefaults = {
        // spread backend first
        ...newUser,
        name: userData.name || newUser.name || userData.emailId?.split('@')[0] || 'New User',
        email: userData.emailId || newUser.email || newUser.emailId,
        isActive: newUser.isActive !== undefined ? newUser.isActive : true,
        isVerified: newUser.isVerified || false,
        // Normalize role from backend user_type
        role: (userData.role || (newUser?.user_type === 'creator' ? 'creator' : 'user')),
        user_type: newUser.user_type
      };

      // Add the new user to the beginning of the users array
      setUsers(prevUsers => [userWithDefaults, ...prevUsers]);
      setFilteredUsers(prev => [userWithDefaults, ...prev]);
      // Also refetch to sync with backend canonical data
      fetchUsers();

      toast.success('User registered successfully');
      return true;
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error.message || 'Registration failed';
      toast.error(errorMessage);
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-t-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading users...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center p-6 max-w-md mx-auto bg-red-500/10 rounded-lg">
          <div className="text-red-400 text-4xl mb-3">⚠️</div>
          <h2 className="text-xl font-semibold text-white mb-2">Error Loading Users</h2>
          <p className="text-gray-300 mb-4">{error}</p>
          <button
            onClick={fetchUsers}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Render empty state
  if (filteredUsers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-center p-6 max-w-md mx-auto">
          <div className="text-gray-400 text-5xl mb-4">👥</div>
          <h2 className="text-xl font-semibold text-white mb-2">No Users Found</h2>
          <p className="text-gray-400 mb-4">
            {searchTerm || statusFilter !== 'all' || roleFilter !== 'all' || verificationFilter !== 'all'
              ? 'Try adjusting your filters or search term.'
              : 'No users have been registered yet.'}
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setRoleFilter('all');
              setVerificationFilter('all');
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
            disabled={!searchTerm && statusFilter === 'all' && roleFilter === 'all' && verificationFilter === 'all'}
          >
            Clear Filters
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      {/* Unified Admin Navbar */}
      <AdminTopNavbar />

      {/* Main Content */}
      <main className="relative z-10 px-6 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header and Actions */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-red-400 via-red-500 to-red-600 bg-clip-text text-transparent">
                User Management
              </h1>
              <p className="text-gray-400">Manage and monitor user accounts across the platform</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:inline-flex items-center px-3 py-2 bg-red-500/20 text-red-400 rounded-lg">
                <Users size={18} className="mr-2" />
                <span className="font-medium">Total: {users.length}</span>
              </div>
              <button
                onClick={(e) => { e.preventDefault(); setShowEditModal(false); setShowAddModal(true); }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                + Add New User
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="admin-card mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="admin-label">Search Users</label>
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  className="admin-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div>
                <label className="admin-label">Filter by Status</label>
                <select
                  className="admin-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Users</option>
                  <option value="active">Active Users</option>
                  <option value="inactive">Inactive Users</option>
                </select>
              </div>
              <div>
                <label className="admin-label">Filter by Role</label>
                <select
                  className="admin-select"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <option value="all">All Roles</option>
                  <option value="user">Users</option>
                  <option value="creator">Creators</option>
                  <option value="creator_pro">Creator Pro</option>
                </select>
              </div>
              <div>
                <label className="admin-label">Filter by Verification Status</label>
                <select
                  className="admin-select"
                  value={verificationFilter}
                  onChange={(e) => setVerificationFilter(e.target.value)}
                >
                  <option value="all">All Verification Status</option>
                  <option value="verified">Verified</option>
                  <option value="unverified">Unverified</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  className="admin-btn admin-btn-secondary w-full"
                  onClick={fetchUsers}
                  disabled={actionLoading}
                >
                  🔄 Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="admin-card">
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Verified</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentUsers.length > 0 ? (
                    currentUsers.map((user) => (
                      <tr key={user._id}>
                        <td>
                          <div className="flex items-center space-x-3">
                            <a href={`mailto:${user.email}`} className="admin-avatar block overflow-hidden rounded-full" title={user.email}>
                              {user.profilePicture ? (
                                <img
                                  src={user.profilePicture}
                                  alt={user.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-red-500/20 flex items-center justify-center text-accent font-semibold">
                                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                </div>
                              )}
                            </a>
                            <div>
                              <div className="font-medium text-primary">
                                {user.name || 'Unknown User'}
                              </div>
                              <div className="text-sm text-muted">
                                ID: {user._id}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="text-primary">{user.email}</td>
                        <td>
                          {user.role === 'creator_pro' ? 'Creator Pro' :
                            user.role === 'creator' ? 'Creator' : 'User'}
                        </td>
                        <td>
                          <span
                            className={`admin-badge ${user.isActive ? 'admin-badge-success' : 'admin-badge-danger'
                              }`}
                          >
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`admin-badge ${user.isVerified ? 'admin-badge-success' : 'admin-badge-danger'
                              }`}
                          >
                            {user.isVerified ? 'Verified' : 'Unverified'}
                          </span>
                        </td>
                        <td className="text-muted">
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleDateString()
                            : 'N/A'}
                        </td>
                        <td>
                          <div className="flex space-x-2">
                            <button
                              className="admin-btn admin-btn-primary admin-btn-sm"
                              onClick={() => handleEditUser(user)}
                              title="Edit User"
                              disabled={actionLoading}
                            >
                              ✏️
                            </button>
                            <button
                              className={`admin-btn ${user.isVerified ? 'admin-btn-warning' : 'admin-btn-success'
                                } admin-btn-sm`}
                              onClick={() => handleToggleVerification(user)}
                              title={user.isVerified ? 'Unverify User' : 'Verify User'}
                              disabled={actionLoading}
                            >
                              {user.isVerified ? '🚫' : '✔️'}
                            </button>
                            <button
                              className={`admin-btn ${user.isActive ? 'admin-btn-warning' : 'admin-btn-success'
                                } admin-btn-sm`}
                              onClick={() => handleToggleActive(user)}
                              title={user.isActive ? 'Deactivate User' : 'Activate User'}
                              disabled={actionLoading}
                            >
                              {user.isActive ? '⏸️' : '▶️'}
                            </button>
                            <button
                              className="admin-btn admin-btn-danger admin-btn-sm"
                              onClick={() => openDeleteModal(user)}
                              title="Delete User"
                              disabled={actionLoading}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-8">
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="admin-pagination">
                <button
                  className="admin-pagination-btn"
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1 || actionLoading}
                >
                  ← Previous
                </button>

                <div className="admin-pagination-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                    <button
                      key={number}
                      className={`admin-pagination-number ${currentPage === number ? 'active' : ''
                        }`}
                      onClick={() => paginate(number)}
                      disabled={actionLoading}
                    >
                      {number}
                    </button>
                  ))}
                </div>

                <button
                  className="admin-pagination-btn"
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages || actionLoading}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3 className="text-lg font-medium text-accent">Edit User</h3>
              <button
                className="admin-modal-close"
                onClick={closeEditModal}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleUpdateUser}>
              <div className="admin-modal-body">
                <div className="space-y-4">
                  <div>
                    <label className="admin-label">Name</label>
                    <input
                      type="text"
                      className="admin-input"
                      value={editFormData.name || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label className="admin-label">Email</label>
                    <input
                      type="email"
                      className="admin-input"
                      value={editFormData.email || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label className="admin-label">Status</label>
                    <select
                      className="admin-select"
                      value={editFormData.isActive}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, isActive: e.target.value === 'true' }))}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="admin-label">Role</label>
                    <select
                      className="admin-select"
                      value={editFormData.role}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, role: e.target.value }))}
                    >
                      <option value="user">User</option>
                      <option value="creator">Creator</option>
                      <option value="creator_pro">Creator Pro</option>
                    </select>
                  </div>
                  <div>
                    <label className="admin-label">Verification Status</label>
                    <select
                      className="admin-select"
                      value={editFormData.isVerified}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, isVerified: e.target.value === 'true' }))}
                    >
                      <option value="true">Verified</option>
                      <option value="false">Unverified</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Updating...' : 'Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3 className="text-lg font-medium text-accent">Confirm Delete</h3>
              <button
                className="admin-modal-close"
                onClick={closeDeleteModal}
              >
                ✕
              </button>
            </div>
            <div className="admin-modal-body">
              <p className="text-primary mb-4">
                Are you sure you want to delete user{' '}
                <strong className="text-accent">{userToDelete?.name}</strong>?
              </p>
              <p className="text-muted text-sm">
                This action cannot be undone. All user data will be permanently removed.
              </p>
            </div>
            <div className="admin-modal-footer">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={closeDeleteModal}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-danger"
                onClick={handleDeleteUser}
                disabled={actionLoading}
              >
                {actionLoading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal select-none">
            <div className="admin-modal-header">
              <h3 className="text-lg font-medium text-accent">Add New User</h3>
              <button className="admin-modal-close" onClick={(e) => { e.preventDefault(); setShowAddModal(false); }}>✕</button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = {
                name: form.name.value,
                emailId: form.emailId.value,
                role: form.role.value,
                password: form.password.value
              };
              if (!formData.password || formData.password.length < 6) {
                toast.error('Password must be at least 6 characters');
                return;
              }
              const ok = await handleRegister(formData);
              if (ok) setShowAddModal(false);
            }}>
              <div className="admin-modal-body">
                <div className="space-y-4">
                  <div>
                    <label className="admin-label">Name</label>
                    <input name="name" type="text" className="admin-input" placeholder="Enter full name" required />
                  </div>
                  <div>
                    <label className="admin-label">Email</label>
                    <input name="emailId" type="email" className="admin-input" placeholder="user@example.com" required />
                  </div>
                  <div>
                    <label className="admin-label">Password</label>
                    <div className="relative">
                      <input name="password" type="password" className="admin-input pr-10" placeholder="Enter password" required minLength={6} />
                      <button
                        type="button"
                        aria-label="Toggle password visibility"
                        className="absolute inset-y-0 right-2 my-auto text-gray-400 hover:text-gray-200"
                        onClick={(e) => {
                          e.preventDefault();
                          const inp = e.currentTarget.parentElement.querySelector('input[name="password"]');
                          if (inp) inp.type = inp.type === 'password' ? 'text' : 'password';
                        }}
                      >
                        👁️
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="admin-label">Role</label>
                    <select name="role" className="admin-select" defaultValue="user">
                      <option value="user">User</option>
                      <option value="creator">Creator</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={(e) => { e.preventDefault(); setShowAddModal(false); }}>Cancel</button>
                <button type="submit" className="admin-btn admin-btn-primary">Add User</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;